import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { faBookmark, faEye, faHeart } from '@fortawesome/free-regular-svg-icons';
import { faCheck, faList } from '@fortawesome/free-solid-svg-icons';
import { MessageService } from 'primeng/api';

import { ENVIRONMENT, Environment } from '@environment';
import { AuthService } from '@usheru-hff/authentication/domain';
import { UserInteractionService } from '@usheru-hff/community-elements/utils';
import { getMovieGenres, getMovieTags, isInMyWishlist, Movie } from '@usheru-hff/shared/domain';
import { FeatureFilmInteractionsToken } from '@usheru-hff/shared/tokens';
import { CommunityElementsService } from '@usheru-hff/shared/utils-community';
import { TrackingService } from '@usheru-hff/sdk/tracking';

enum FILM_INTERATION_EVENTS {
  addMovieToWishList = 'AddToWishlist',
  removeMovieFromWishlist = 'RemoveFromWishlist',
  addToWatched = 'AddToWatched',
  removeFromWatched = 'RemoveFromWatched',
  addMovieToList = 'AddMovieToList'
}
@Component({
  selector: 'feature-film-interactions',
  templateUrl: './feature-film-interactions.component.html',
  styleUrls: ['./feature-film-interactions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: FeatureFilmInteractionsToken, useExisting: FeatureFilmInteractionsComponent }]
})
export class FeatureFilmInteractionsComponent extends FeatureFilmInteractionsToken implements OnInit {
  @Input() movie: Movie | undefined;
  @Input() orientation: 'horizontal' | 'vertical' = 'horizontal';

  readonly TRACKING_CATEGORY = 'Community';

  waitingForWatchedMovieResponse = false;
  waitingForWishlistResponse = false;
  isMovieInMyWishList = isInMyWishlist;

  wishlistHint: string;
  watchedHint: string;
  rateHint: string;
  shareHint: string;
  addToListHint: string;

  bookmarkIcon = faBookmark;
  eyeIcon = faEye;
  checkIcon = faCheck;
  hearthIcon = faHeart;
  listIcon = faList;

  hintOptions: any = {
    tooltipPosition: 'left',
    showDelay: 500
  };

  userSig = toSignal(this.authService.$userStatus);

  constructor(
    private userInteractionService: UserInteractionService,
    private messageService: MessageService,
    private authService: AuthService,
    private cd: ChangeDetectorRef,
    @Inject(ENVIRONMENT) private env: Environment,
    private communityElementsService: CommunityElementsService,
    private trackingService: TrackingService
  ) {
    super();
  }

  ngOnInit(): void {
    this.setUpHints();
  }

  private setUpHints() {
    this.wishlistHint = isInMyWishlist(this.movie)
      ? $localize`:@@feature-film-interactions.wishlist.remove.tooltip:Remove movie from your wishlist`
      : $localize`:@@feature-film-interactions.wishlist.add.tooltip:Save movie to your wishlist`;
    this.watchedHint = this.movie.inMyWatched
      ? $localize`:@@feature-film-interactions.watched.remove.tooltip:Unmark movie as watched`
      : $localize`:@@feature-film-interactions.watched.add.tooltip:Mark movie as watched`;
    this.rateHint = this.movie.myRank
      ? $localize`:@@feature-film-interactions.rating.remove.tooltip:Change movie rating`
      : $localize`:@@feature-film-interactions.rating.add.tooltip:Rate movie`;
    this.shareHint = $localize`:@@feature-film-interactions.share.tooltip:Share movie`;
    this.addToListHint = $localize`:@@feature-film-interactions.addToList.tooltip:Add movie to your list`;
  }

  /**
   * @description This function will be called when the user clicks on the heart button and will open up the rating modal
   * Rating a movie will use the userInteractionService addMovieToWatched function
   * @param user
   */
  onRateMovie() {
    // open rating modal
    this.userInteractionService.getMovieRating(this.movie);
  }

  onMarkMovieAsWatched() {
    this.waitingForWatchedMovieResponse = true;
    if (this.movie.inMyWatched) {
      this.removeWatchedMovie();
    } else {
      this.userInteractionService.addMovieToWatched({ idMovie: this.movie.id }).subscribe({
        next: () => {
          this.trackFilmInteractionEvent(FILM_INTERATION_EVENTS.addToWatched);
          this.messageService.add({
            severity: 'success',
            summary: $localize`:@@feature-film-interactions.watched.success.title:Success`,
            detail: $localize`:@@feature-film-interactions.watched.success.description:Movie marked as watched`
          });
          this.waitingForWatchedMovieResponse = false;
          this.movie.inMyWatched = true;
          this.cd.markForCheck();
          this.processChange();
        },
        error: () => {
          this.waitingForWatchedMovieResponse = false;
          this.messageService.add({
            severity: 'error',
            summary: $localize`:@@feature-film-interactions.watched.error.title:Error`,
            detail: $localize`:@@feature-film-interactions.watched.error.description:Movie could not be added to watched`
          });
          this.processChange();
        }
      });
    }
  }

  private removeWatchedMovie() {
    this.waitingForWatchedMovieResponse = true;
    this.userInteractionService.removeMovieFromWatched(this.movie.id).subscribe({
      next: () => {
        this.trackFilmInteractionEvent(FILM_INTERATION_EVENTS.removeFromWatched);
        this.messageService.add({
          severity: 'success',
          summary: $localize`:@@feature-film-interactions.unwatched.success.title:Success`,
          detail: $localize`:@@feature-film-interactions.unwatched.success.description:Movie removed from watched`
        });
        this.waitingForWatchedMovieResponse = false;
        this.movie.inMyWatched = false;
        this.processChange();
      },
      error: () => {
        this.waitingForWatchedMovieResponse = false;
        this.messageService.add({
          severity: 'error',
          summary: $localize`:@@feature-film-interactions.unwatched.error.title:Error`,
          detail: $localize`:@@feature-film-interactions.unwatched.error.description:Movie could not be removed from watched`
        });
        this.processChange();
      }
    });
  }

  processChange() {
    this.cd.markForCheck();
    this.setUpHints();
  }

  addMovieToList() {
    // open search list modal to add movie to list
    // todo: get user from auth service

    this.userInteractionService.searchMyLists(this.movie, this.userSig());
  }
  onSharing() {
    this.userInteractionService.shareMovieDetail(this.movie);
  }

  onAddMovieToWishlist() {
    this.waitingForWishlistResponse = true;
    if (isInMyWishlist(this.movie)) {
      this.removeMovieFromWishlist();
      return;
    } else {
      // add film to watchlist
      this.userInteractionService.addMovieToWishList(this.movie?.id).subscribe({
        next: () => {
          this.trackFilmInteractionEvent(FILM_INTERATION_EVENTS.addMovieToWishList);
          this.messageService.add({
            severity: 'success',
            summary: $localize`:@@feature-film-interactions.wishlisted.success.title:Success`,
            detail: $localize`:@@feature-film-interactions.wishlisted.success.description:Movie added to wishlist`
          });
          this.waitingForWishlistResponse = false;
          this.movie.inMyWhislist = true;
          this.movie.inMyWishlist = true;
          this.processChange();
        },
        error: () => {
          this.waitingForWishlistResponse = false;
          this.messageService.add({
            severity: 'error',
            summary: $localize`:@@feature-film-interactions.wishlisted.error.title:Error`,
            detail: $localize`:@@feature-film-interactions.wishlisted.error.description:Movie could not be added to wishlist`
          });
          this.processChange();
        }
      });
    }

    // track event
  }

  private removeMovieFromWishlist() {
    this.userInteractionService.removeMovieFromWishList(this.movie?.id).subscribe({
      next: () => {
        this.trackFilmInteractionEvent(FILM_INTERATION_EVENTS.removeMovieFromWishlist);
        this.messageService.add({
          severity: 'success',
          summary: $localize`:@@feature-film-interactions.unwishlisted.success.title:Success`,
          detail: $localize`:@@feature-film-interactions.unwishlisted.success.description:Movie removed from wishlist`
        });
        this.waitingForWishlistResponse = false;
        this.movie.inMyWhislist = false;
        this.movie.inMyWishlist = false;
        this.processChange();
      },
      error: () => {
        this.waitingForWishlistResponse = false;
        this.messageService.add({
          severity: 'error',
          summary: $localize`:@@feature-film-interactions.unwishlisted.error.title:Error`,
          detail: $localize`:@@feature-film-interactions.unwishlisted.sucess.description:Movie could not be removed from wishlist`
        });
        this.processChange();
      }
    });
  }

  trackFilmInteractionEvent(action: FILM_INTERATION_EVENTS) {
    this.trackingService.trackEvent({
      action: action,
      properties: {
        category: this.TRACKING_CATEGORY,
        // label: '',
        movie: this.movie.title || '',
        idMovie: this.movie.id,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie),
        genresMovie: getMovieGenres(this.movie),
        user: this.username
      }
    });
  }

  get watchedIcon() {
    const selectedIcon = this.env.communityConfig?.filmInteractionsConfig?.watchedIcon || 'check';
    return selectedIcon === 'check' ? this.checkIcon : this.eyeIcon;
  }

  get ratingEnabled() {
    return this.communityElementsService.isFeatureEnabled('rating');
  }

  get username() {
    return this.authService.getUserUsername();
  }

  // get watchedIconSolid() {}
}
